import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Card,
  CardContent,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SecurityIcon from "@mui/icons-material/Security";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { getAdminAccountDetails } from "../../../../services/Admin/AdminUsersAccountsApi";

const AccountDetailsDialog = ({ open, onClose, adminId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [admin, setAdmin] = useState(null);

  useEffect(() => {
    if (open && adminId) {
      fetchAccountDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, adminId]);

  const fetchAccountDetails = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getAdminAccountDetails(adminId);
      setAdmin(response.data);
    } catch (err) {
      console.error("خطأ في جلب تفاصيل الحساب:", err);
      setError(err.message || "حدث خطأ في جلب تفاصيل الحساب");
    } finally {
      setLoading(false);
    }
  };

  const getPermissionChips = (permissions) => {
    if (!permissions) return [];

    const permissionsList = [
      { key: "view", label: "عرض", icon: "👁️" },
      { key: "add", label: "إضافة", icon: "➕" },
      { key: "edit", label: "تعديل", icon: "✏️" },
      { key: "delete", label: "حذف", icon: "🗑️" },
      { key: "print", label: "طباعة", icon: "🖨️" },
      { key: "export", label: "تصدير", icon: "📤" },
    ];

    return permissionsList
      .filter((perm) => permissions && permissions[perm.key] === 1)
      .map((perm) => ({ ...perm, enabled: true }))
      .concat(
        permissionsList.filter((perm) => !permissions || permissions[perm.key] !== 1)
          .map((perm) => ({ ...perm, enabled: false }))
      );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "غير متوفر";
    return new Date(dateString).toLocaleDateString("ar-EG", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getRoleLabel = (role) => {
    const roles = {
      admin: "مدير نظام",
      user: "مستخدم",
      captain_order: "قائد الطلب",
      delivery: "سائق التوصيل",
      cashier: "الكاشير",
      kitchen: "المطبخ",
      manager: "مدير",
      head_manager: "مدير عام",
    };
    return roles[role] || role;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      dir="rtl"
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          fontWeight: "bold",
          textAlign: "right",
          display: "flex",
          alignItems: "center",
          gap: 1,
          backgroundColor: "info.light",
          color: "info.dark",
        }}
      >
        <InfoIcon />
        تفاصيل الحساب
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px",
            }}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : admin ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Header with Avatar */}
            <Card
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      backgroundColor: "white",
                      color: "primary.main",
                      fontSize: "2rem",
                      fontWeight: "bold",
                    }}
                  >
                    {admin.name.charAt(0)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: "bold", mb: 0.5 }}>
                      {admin.name}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                      <Chip
                        label={getRoleLabel(admin.role)}
                        size="small"
                        sx={{
                          backgroundColor: "rgba(255,255,255,0.2)",
                          color: "white",
                          fontWeight: "bold",
                        }}
                      />
                      {admin.is_active === 1 ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="نشط"
                          size="small"
                          sx={{
                            backgroundColor: "rgba(76, 175, 80, 0.2)",
                            color: "white",
                            fontWeight: "bold",
                          }}
                        />
                      ) : (
                        <Chip
                          icon={<CancelIcon />}
                          label="غير نشط"
                          size="small"
                          sx={{
                            backgroundColor: "rgba(244, 67, 54, 0.2)",
                            color: "white",
                            fontWeight: "bold",
                          }}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      ID: {admin.id}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Basic Information */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                <PersonIcon /> البيانات الأساسية
              </Typography>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", width: "30%", borderBottom: "1px solid #eee" }}>
                      اسم المستخدم:
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid #eee" }}>@{admin.username}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", width: "30%", borderBottom: "1px solid #eee" }}>
                      البريد الإلكتروني:
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid #eee" }}>
                      {admin.email || "غير متوفر"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", width: "30%", borderBottom: "1px solid #eee" }}>
                      نوع الحساب:
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid #eee" }}>
                      <Chip label={admin.type === "admin" ? "إدارة" : admin.type} size="small" />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>

            {/* Contact Information */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                <PhoneIcon /> بيانات الاتصال
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {admin.phone && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography sx={{ fontWeight: "bold", minWidth: "80px" }}>الهاتف 1:</Typography>
                    <Chip label={admin.phone} variant="outlined" />
                  </Box>
                )}
                {admin.phone2 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography sx={{ fontWeight: "bold", minWidth: "80px" }}>الهاتف 2:</Typography>
                    <Chip label={admin.phone2} variant="outlined" />
                  </Box>
                )}
                {admin.phone3 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography sx={{ fontWeight: "bold", minWidth: "80px" }}>الهاتف 3:</Typography>
                    <Chip label={admin.phone3} variant="outlined" />
                  </Box>
                )}
              </Box>
            </Paper>

            {/* Address Information */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                <LocationOnIcon /> العناوين
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {admin.address && (
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Typography sx={{ fontWeight: "bold", minWidth: "80px" }}>العنوان 1:</Typography>
                    <Typography sx={{ flex: 1 }}>{admin.address}</Typography>
                  </Box>
                )}
                {admin.address2 && (
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Typography sx={{ fontWeight: "bold", minWidth: "80px" }}>العنوان 2:</Typography>
                    <Typography sx={{ flex: 1 }}>{admin.address2}</Typography>
                  </Box>
                )}
                {admin.address3 && (
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Typography sx={{ fontWeight: "bold", minWidth: "80px" }}>العنوان 3:</Typography>
                    <Typography sx={{ flex: 1 }}>{admin.address3}</Typography>
                  </Box>
                )}
              </Box>
            </Paper>

            {/* Permissions */}
            {admin.permissions && (
              <Paper sx={{ p: 2, backgroundColor: "#f9f9f9" }}>
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                  <SecurityIcon /> الصلاحيات
                </Typography>
                <Grid container spacing={1.5}>
                  {getPermissionChips(admin.permissions).map((perm) => (
                    <Grid item xs={6} sm={4} key={perm.key}>
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          border: "2px solid",
                          borderColor: perm.enabled ? "success.main" : "divider",
                          backgroundColor: perm.enabled ? "success.light" : "background.paper",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: "bold",
                            color: perm.enabled ? "success.dark" : "text.secondary",
                          }}
                        >
                          {perm.icon} {perm.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: perm.enabled ? "success.dark" : "text.secondary",
                            display: "block",
                            mt: 0.5,
                          }}
                        >
                          {perm.enabled ? "مفعل" : "معطل"}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )}

            <Divider />

            {/* Dates Information */}
            <Paper sx={{ p: 2, backgroundColor: "#f0f7ff" }}>
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                <CalendarTodayIcon /> المعلومات الزمنية
              </Typography>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", width: "30%", borderBottom: "none" }}>
                      تاريخ الإنشاء:
                    </TableCell>
                    <TableCell sx={{ borderBottom: "none" }}>{formatDate(admin.created_at)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", width: "30%", borderBottom: "none" }}>
                      آخر تحديث:
                    </TableCell>
                    <TableCell sx={{ borderBottom: "none" }}>{formatDate(admin.updated_at)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          </Box>
        ) : (
          <Alert severity="warning">لم يتم العثور على البيانات</Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="contained" color="primary">
          إغلاق
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountDetailsDialog;
